import moment from "moment";
import UserService from "api/user/user";
import {useEffect, useMemo, useRef, useState} from 'react';
import {STATS_QUOTE_CA} from "v4/data/apiRoutes";
import useFetch from "v4/hooks/useFetch";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import {generateUrl} from "v4/services/api.service";
import ReactTooltip from "react-tooltip";

export default function useDashboardQuotesGraph(isInView) {
    const {t} = usePolTranslation();
    const [dateInterval, setDateInterval] = useState({
        beginAt: moment().startOf('month').format('YYYY-MM-DD'),
        endAt: moment().endOf('month').add(5, 'months').format('YYYY-MM-DD'),
    });

    const chartRef = useRef(null);
    const tooltipAnchorRef = useRef(null);

    const months = useMemo(() => {
        const start = moment(dateInterval.beginAt);
        const end = moment(dateInterval.endAt);
        const monthsArray = [];
        let current = start.clone();

        while (current.isSameOrBefore(end, 'month')) {
            const monthString = new Intl.DateTimeFormat(UserService.getLocale(), {month: 'long'}).format(current.toDate());
            monthsArray.push({
                label: monthString.slice(0, 1).toUpperCase() + monthString.slice(1), // Capitalize
                monthNumber: current.month() + 1,
                year: current.year(),
                isCurrent: current.isSame(moment(), 'month') && current.isSame(moment(), 'year'),
            });
            current.add(1, 'month');
        }

        return monthsArray;
    }, [dateInterval]);

    const [{data, isLoading}, fetchQuoteGraph] = useFetch();
    useEffect(() => {
        if (!isInView) return;

        fetchQuoteGraph({
            url: generateUrl(STATS_QUOTE_CA, {
                beginAt: dateInterval.beginAt,
                endAt: dateInterval.endAt,
            }),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateInterval, isInView])

    const quotesData = useMemo(() => {
        const [signed_quotes, in_progress_quotes, in_progress_quotes_weighted] = months.reduce((acc, month) => {
            const monthData = data?.find(({mois, annee}) => {
                return parseInt(mois, 10) === month.monthNumber && parseInt(annee, 10) === month.year;
            });

            acc[0].push(monthData?.signed ?? 0);
            acc[1].push(monthData?.previsional ?? 0);
            acc[2].push(monthData?.weighted ?? 0);

            return acc;
        }, [[], [], []]);

        return {
            signed_quotes,
            in_progress_quotes,
            in_progress_quotes_weighted,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const previousMonth = () => {
        const newBeginAt = moment(dateInterval.beginAt).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        const newEndAt = moment(dateInterval.endAt).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        setDateInterval({beginAt: newBeginAt, endAt: newEndAt});
    }

    const nextMonth = () => {
        const newBeginAt = moment(dateInterval.beginAt).add(1, 'month').startOf('month').format('YYYY-MM-DD');
        const newEndAt = moment(dateInterval.endAt).add(1, 'month').endOf('month').format('YYYY-MM-DD');
        setDateInterval({beginAt: newBeginAt, endAt: newEndAt});
    }

    const resetDateInterval = () => {
        setDateInterval({
            beginAt: moment().startOf('month').format('YYYY-MM-DD'),
            endAt: moment().endOf('month').add(5, 'months').format('YYYY-MM-DD'),
        });
    }

    const canPreviousMonth = moment(dateInterval.beginAt).isAfter(moment().subtract(12, 'months'), 'month');

    const canNextMonth = moment(dateInterval.endAt).isBefore(moment().add(12, 'months'), 'month');

    const showResetButton = !moment(dateInterval.beginAt).isSame(moment().startOf('month'), 'month');

    const onButtonHover = (buttonInfo) => {
        if (buttonInfo?.tooltipText && tooltipAnchorRef.current) {
            tooltipAnchorRef.current.style.display = 'block';
            tooltipAnchorRef.current.style.left = `${buttonInfo.x}px`;
            tooltipAnchorRef.current.style.top = `${buttonInfo.y}px`;
            tooltipAnchorRef.current.style.height = `${buttonInfo.radius * 2}px`;
            tooltipAnchorRef.current.style.width = `${buttonInfo.radius * 2}px`;
            tooltipAnchorRef.current.style.transform = 'translate(-50%, -50%)';
            tooltipAnchorRef.current.setAttribute('data-tip', t(buttonInfo.tooltipText));
            !buttonInfo.disabled && ReactTooltip.show(tooltipAnchorRef.current);
        } else {
            tooltipAnchorRef.current.style.display = 'none';
            ReactTooltip.hide(tooltipAnchorRef.current);
        }
    };

    return {
        chartRef,
        tooltipAnchorRef,
        data: {
            labels: months.map(month => `${month.label} ${month.year}`),
            datasets: [
                {
                    label: t('signed_sales'),
                    backgroundColor: 'rgb(55, 46, 116)',
                    borderColor: 'rgb(55, 46, 116)',
                    data: quotesData.signed_quotes,
                },
                {
                    label: t('sales_previsions'),
                    backgroundColor: 'rgb(200, 190, 254)',
                    borderColor: 'rgb(200, 190, 254)',
                    data: quotesData.in_progress_quotes,
                },
                {
                    label: t('weighted_sales_previsions'),
                    backgroundColor: 'rgb(237, 116, 147)',
                    borderColor: 'rgb(237, 116, 147)',
                    data: quotesData.in_progress_quotes_weighted,
                }
            ]
        },
        previousMonth,
        nextMonth,
        resetDateInterval,
        showResetButton,
        canPreviousMonth,
        canNextMonth,
        isLoading,
        onButtonHover,
    };
}
